import { useEffect, useState } from "react";
import { AppProvider } from "@shopify/polaris";
import enTranslations from "@shopify/polaris/locales/en.json";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { NavMenu } from "@shopify/app-bridge-react";

import ErrorBoundary from "./components/ErrorBoundary";
import ReconnectBanner from "./components/ReconnectBanner";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Rules from "./pages/Rules";
import RuleEditor from "./pages/RuleEditor";
import Settings from "./pages/Settings";
import Orders from "./pages/Orders";
import Activity from "./pages/Activity";
import { api } from "./api";
import { identifyAuthenticatedUser } from "./posthog";
import type { CredentialSummary } from "./types";

function AppShell(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>(null);

  useEffect(() => {
    api
      .get<{ user_id: string }>("/identity")
      .then(({ user_id }) => identifyAuthenticatedUser(user_id))
      // The app remains usable when analytics identity cannot be established.
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    api
      .get<{ credentials: CredentialSummary[] }>("/credentials")
      .then(({ credentials }) => {
        // Onboarding is done once ShipPromise can both buy a label (EasyPost key)
        // and print it (PrintNode key + the shop-wide default printer, which
        // lives on that credential's metadata).
        const easypost = credentials.find((c) => c.kind === "easypost");
        const printnode = credentials.find((c) => c.kind === "printnode");
        setOnboardingComplete(Boolean(easypost) && Boolean(printnode?.metadata?.default_printer_id));
      })
      .catch(() => setOnboardingComplete(false));
  }, []);

  useEffect(() => {
    if (onboardingComplete === false && location.pathname !== "/onboarding") {
      navigate("/onboarding", { replace: true });
    } else if (onboardingComplete === true && location.pathname === "/onboarding") {
      navigate("/", { replace: true });
    }
  }, [onboardingComplete, location.pathname, navigate]);

  return (
    <>
      {/*
        NavMenu is rendered by Shopify admin OUTSIDE our iframe as part of
        the sidebar navigation. This is the modern App Bridge v4 pattern.
        We do NOT wrap our routes in Polaris <Frame> / <Navigation> — those
        are deprecated for embedded apps and cause silent unmount bugs.
      */}
      {/*
        Per Shopify docs, NavMenu children must be plain <a> tags, not
        react-router <Link>. App Bridge intercepts anchor clicks and
        routes them client-side via its Navigation API. Using <Link>
        prevents Shopify admin from registering some items (e.g. Settings)
        in the outer sidebar.
        https://shopify.dev/docs/api/app-home/app-bridge-web-components/app-nav
      */}
      <NavMenu>
        <a href="/" rel="home">Dashboard</a>
        <a href="/rules">Rules</a>
        <a href="/orders">Orders</a>
        <a href="/activity">Activity</a>
        <a href="/settings">Settings</a>
      </NavMenu>

      {/*
        Outside the ErrorBoundary and outside Routes: an unreadable access token
        breaks every page, so the prompt to fix it must survive a page crash and
        must not be re-mounted (and re-hidden) by navigation.
      */}
      <ReconnectBanner />

      <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="/onboarding" element={<Onboarding onComplete={() => setOnboardingComplete(true)} />} />
          <Route path="/" element={<Dashboard />} />
          <Route path="/rules" element={<Rules />} />
          <Route path="/rules/new" element={<RuleEditor />} />
          <Route path="/rules/:id" element={<RuleEditor />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </ErrorBoundary>
    </>
  );
}

// App Bridge v4 initializes automatically from index.html:
// - <meta name="shopify-api-key" content="..."> supplies the API key
// - <script src="https://cdn.shopify.com/shopifycloud/app-bridge.js"> initializes
// - window.shopify is available globally (used by ./api)
// No <Provider> wrapper needed.
export default function App(): JSX.Element {
  return (
    <AppProvider i18n={enTranslations}>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </AppProvider>
  );
}
